class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.swiperInstances = new Map();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search-results');
    this.defaultSearchResults = this.querySelector('#default-search-results');
    this.clearButton = this.querySelector('.search__clear-button');
    this.cachedResults = {};
    this.abortController = null;
    this.debounceTimer = null;
    this.setupEventListeners();
    this.showDefaultResults();
    this.setupSuggestedProducts();
  }

  setupEventListeners() {
    this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
    this.input.form.addEventListener('reset', this.onFormReset.bind(this));
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));
    this.clearButton.addEventListener('click', this.onClearButtonClick.bind(this));
  }

  debounce(fn, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.showDefaultResults();
      this.hideOrShowClearButton();
      return;
    }

    this.hideOrShowClearButton();
    this.getSearchResults(searchTerm);
  }

  onClearButtonClick() {
    this.input.value = '';
    this.input.focus();
    this.showDefaultResults();
    this.hideOrShowClearButton();
  }

  hideOrShowClearButton() {
    this.clearButton.style.display = this.input.value.length > 0 ? 'block' : 'none';
  }

  onFormReset(event) {
    this.input.value = '';
    this.showDefaultResults();
  }

  onFormSubmit(event) {
    event.preventDefault();
    const searchTerm = this.input.value.trim();
    if (searchTerm.length > 0) {
      this.getSearchResults(searchTerm);
    }
  }

  onKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const searchTerm = this.input.value.trim();
      if (searchTerm.length > 0) {
        this.getSearchResults(searchTerm);
      }
    }
  }

  setupSuggestedProducts() {
    const suggestedButtons = this.querySelectorAll('.suggested-product-button');
    suggestedButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const searchTerm = button.dataset.searchTerm;
        this.input.value = searchTerm;
        this.onChange();
      });
    });
  }

  async getSearchResults(searchTerm) {
    // キャッシュされた結果がある場合はそれを使用
    if (this.cachedResults[searchTerm]) {
      await this.renderSearchResults(this.cachedResults[searchTerm]);
      return;
    }

    // 既存のリクエストをキャンセル
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      type: 'product,article',
      view: 'ajax'
    });

    const url = `/search?${params.toString()}`;

    fetch(url, { signal: this.abortController.signal })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then(async html => {
        // 結果をキャッシュし、非同期でレンダリング
        this.cachedResults[searchTerm] = html;
        await this.renderSearchResults(html);
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return;
        }
        this.renderError('検索中にエラーが発生しました。もう一度お試しください。');
      });
  }

  async renderSearchResults(html) {
    if (html.trim()) {
      const tempElement = document.createElement('div');
      tempElement.innerHTML = html;
  
      const searchResultsContainer = tempElement.querySelector('.search-results-container');

      if (searchResultsContainer) {
        const faqResults = searchResultsContainer.querySelector('.search-results-faq');
        const productResults = searchResultsContainer.querySelector('.search-results-products');
  
        // 検索結果がある場合のみ表示
        if (faqResults || productResults) {
          // DOM更新を非同期で行い、レンダリングの完了を待つ
          await new Promise(resolve => {
            this.predictiveSearchResults.innerHTML = searchResultsContainer.innerHTML;
            requestAnimationFrame(resolve);
          });
          
          if (faqResults) {
            const faqSlider = this.predictiveSearchResults.querySelector('.faq-slider');
            const faqSlides = faqSlider ? faqSlider.querySelectorAll('.swiper-slide') : [];

            if (faqSlides.length === 0) {
              // FAQスライドが存在しない場合、FAQセクションを削除
              const faqSection = this.predictiveSearchResults.querySelector('.search-results-faq');
              if (faqSection) {
                faqSection.remove();
              }
            } else {
              // FAQスライドが存在する場合、Swiperの初期化とFAQボタンの設定を行う
              this.initializeSwiperSliders(this.predictiveSearchResults);
              this.initializeFAQButtons(this.predictiveSearchResults);
            }
          }
        } else {
          // 検索結果が見つからない場合のメッセージを表示
          this.predictiveSearchResults.innerHTML = '<p>検索結果が見つかりませんでした。</p>';
        }
      } else {
        // 検索結果コンテナが見つからない場合のメッセージを表示
        this.predictiveSearchResults.innerHTML = '<p>検索結果が見つかりませんでした。</p>';
      }
    } else {
      // 空のHTMLコンテンツの場合のメッセージを表示
      this.predictiveSearchResults.innerHTML = '<p>検索結果が見つかりませんでした。</p>';
    }
  
    this.showSearchResults();
  }

  showDefaultResults() {
    this.defaultSearchResults.style.display = 'block';
    this.predictiveSearchResults.style.display = 'none';
    this.initializeSwiperSliders(this.defaultSearchResults);
    this.initializeFAQButtons(this.defaultSearchResults);
  }

  showSearchResults() {
    this.defaultSearchResults.style.display = 'none';
    this.predictiveSearchResults.style.display = 'block';
  }

  initializeFAQButtons(container) {
    const faqResults = container.querySelectorAll('.search-result.faq-result');
  
    faqResults.forEach((result) => {
      const wrapper = result.querySelector('.faq-result__wrapper');
      const content = wrapper.querySelector('.faq-content');
      const textContainer = content.querySelector('.faq-text');
      const btn = wrapper.querySelector('.faq-more-btn');
  
      if (!textContainer || !btn) return;
  
      // ボタンの表示/非表示を更新する関数
      const updateButtonVisibility = () => {
        // コンテンツの実際の高さを取得
        const contentHeight = content.scrollHeight;
        const shouldShowButton = contentHeight > 100;
  
        wrapper.classList.toggle('has-button', shouldShowButton);
        btn.style.display = shouldShowButton ? 'block' : 'none';
      };
  
      // 初期チェック
      updateButtonVisibility();
  
      // ResizeObserverを使用してcontentのサイズ変更を検出
      const resizeObserver = new ResizeObserver(updateButtonVisibility);
      resizeObserver.observe(content);
  
      btn.addEventListener('click', () => {
        wrapper.classList.toggle('expanded');
        if (wrapper.classList.contains('expanded')) {
          wrapper.style.height = `${content.scrollHeight + 50}px`; // 50pxはボタンの高さ分
          btn.textContent = 'Read less';
        } else {
          wrapper.style.height = '110px';
          btn.textContent = 'Read more';
        }
  
        const swiper = container.querySelector('.swiper')?.swiper;
        if (swiper) {
          // Swiperの更新とプログレスバーの更新を遅延実行
          setTimeout(() => {
            swiper.update();
            this.updateProgressBar(swiper);
          }, 300); // CSSトランジションの時間に合わせて調整可能
        }
      });
    });
  }

  renderError(message) {
    this.predictiveSearchResults.innerHTML = `<p>${message}</p>`;
    this.showSearchResults();
  }

  initializeSwiperSliders(container) {
    const faqSliders = container.querySelectorAll('.faq-slider');

    faqSliders.forEach((faqSlider, index) => {
      if (faqSlider) {
        // Swiperが定義されているかチェック
        if (typeof Swiper === 'undefined') {
          return;
        }

        // 既存のSwiperインスタンスがあればそれを破棄する
        if (this.swiperInstances.has(faqSlider)) {
          this.swiperInstances.get(faqSlider).destroy();
        }
        
        // 新しいSwiperインスタンスを生成
        try {
          const swiper = new Swiper(faqSlider, {
            slidesPerView: 2,
            spaceBetween: 10,
            on: {
              init: (swiper) => {
                this.initProgressBar(swiper);
                this.showAllSlides(swiper);
              },
              slideChange: (swiper) => {
                this.updateProgressBar(swiper);
              },
            },
          });
          
          // 新しいインスタンスを保存
          this.swiperInstances.set(faqSlider, swiper);
          
          this.initProgressBar(swiper);
        } catch (error) {
          console.error(`Error initializing Swiper for FAQ Slider ${index + 1}:`, error);
        }
      }
    });
  }

  showAllSlides(swiper) {
    swiper.slides.forEach(slide => {
      slide.style.display = '';
    });
    swiper.update();
  }

  initProgressBar(swiper) {
    const progressBar = swiper.el.closest('.search-results-faq').querySelector('.faq-progress');
    if (progressBar && swiper.slides.length > 2) {
      const initialWidth = (1 / (swiper.slides.length - 1)) * 100;
      progressBar.style.width = `${initialWidth}%`;
    }
  }
  
  updateProgressBar(swiper) {
    const progressBar = swiper.el.closest('.search-results-faq').querySelector('.faq-progress');
    if (progressBar && swiper.slides.length > 2) {
      const totalSlides = swiper.slides.length - 1;
      const progress = ((swiper.activeIndex + 1) / totalSlides) * 100;
      progressBar.style.width = `${Math.min(progress, 100)}%`;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);